import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { ProcessedSalesItem } from "../hooks/useSalesByItemData";
import { formatNumber } from "../utils/format";
import { SERIES, GRID, AXIS_TICK } from "../utils/chartTheme";

interface SalesByItemChartProps {
  items: ProcessedSalesItem[];
  maxItems?: number;
  height?: number;
}

// Kept local (not the shared compact formatter): the tooltip formats per-item
// gross profit, which can be negative, and negatives here fall through to
// full "$-1,234" rendering — the shared compact would change that text.
function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload?.[0]) return null;
  const data = payload[0].payload as ProcessedSalesItem;
  return (
    <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-xs max-w-xs">
      <div className="font-medium mb-1 truncate">
        {data.display_name || data.item_name}
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-gray-400">Revenue</span>
        <span className="font-medium tabular-nums">
          {formatCurrency(data.revenue)}
        </span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-gray-400">Cost</span>
        <span className="font-medium tabular-nums">
          {formatCurrency(data.cost)}
        </span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-gray-400">Gross Profit</span>
        <span className="font-medium tabular-nums">
          {formatCurrency(data.grossProfit)}
        </span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-gray-400">Margin</span>
        <span className="font-medium tabular-nums">
          {data.grossMargin.toFixed(1)}%
        </span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-gray-400">Quantity</span>
        <span className="font-medium tabular-nums">
          {formatNumber(data.quantity)}
        </span>
      </div>
    </div>
  );
}

export function SalesByItemChart({
  items,
  maxItems = 10,
  height = 320,
}: SalesByItemChartProps) {
  const chartData = useMemo(() => {
    return items.slice(0, maxItems).map((item) => ({
      ...item,
      // Truncate long names for Y-axis
      label:
        (item.display_name || item.item_name).length > 28
          ? (item.display_name || item.item_name).slice(0, 25) + "..."
          : item.display_name || item.item_name,
    }));
  }, [items, maxItems]);

  if (chartData.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-sm text-gray-400"
        style={{ height }}
      >
        No sales data available
      </div>
    );
  }

  // Compute dynamic height: ~32px per bar + padding
  const dynamicHeight = Math.max(height, chartData.length * 32 + 40);

  return (
    <ResponsiveContainer width="100%" height={dynamicHeight}>
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ top: 4, right: 30, left: 10, bottom: 4 }}
      >
        <CartesianGrid {...GRID} horizontal={false} />
        <XAxis
          type="number"
          tickFormatter={(v) => formatCurrency(v)}
          tick={{ fontSize: 11, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey="label"
          width={180}
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: GRID.stroke }} />
        <Bar dataKey="revenue" radius={[0, 4, 4, 0]} maxBarSize={24}>
          {chartData.map((_, index) => (
            <Cell
              key={index}
              fill={
                index === 0 ? SERIES.primary : index < 3 ? "#6B9FCE" : "#94BDE0"
              }
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
